/*
 * Venuemanager assistant: a chat drawer where the admin asks about their own club. The answers come from
 * the backend (falcon functions/assistant.js, callables assistant-send/-resolve/-list/-delete), which scopes
 * everything to the club in the caller's token.
 *
 * The conversation is customers/{cid}/assistant/{id}/messages, read with onSnapshot: while a turn runs the
 * server writes the tool lines ("Haen varaukset…") and interim texts there, so the admin sees progress
 * before the final answer arrives.
 *
 * Changes are never made by the model directly: it makes a proposal, the server works out what would happen
 * (customers/{cid}/assistant/{id}/proposals/{pid}: summary, facts, warnings) and the card below shows exactly
 * that. Hyväksy / Hylkää calls assistant-resolve; the server executes from its own copy, not from this one.
 */
import React, { useEffect, useRef, useState } from 'react'
import moment from 'moment'
import MarkdownIt from 'markdown-it'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Fade,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Modal,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import HistoryIcon from '@mui/icons-material/History'
import DeleteIcon from '@mui/icons-material/Delete'
import BuildIcon from '@mui/icons-material/Build'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome'
import { keyframes } from '@mui/system'

// html: false - the model's text is never trusted as HTML. No images either: the answer can echo text
// customers wrote, and an image URL would be fetched on render, sending whatever is in it to that host.
const md = new MarkdownIt({ html: false, linkify: false, breaks: true }).disable(['image'])

// links open in a new tab, so following one does not leave the app
const defaultLinkOpen = md.renderer.rules.link_open || ((tokens, idx, options, env, self) => self.renderToken(tokens, idx, options))
md.renderer.rules.link_open = (tokens, idx, options, env, self) => {
  tokens[idx].attrSet('target', '_blank')
  tokens[idx].attrSet('rel', 'noopener noreferrer')
  return defaultLinkOpen(tokens, idx, options, env, self)
}

// The window floats in the middle of the screen and grows both ways as the chat fills, up to the
// viewport less this margin; after that its message area scrolls.
const MARGIN = 24

// Gemini-like colours for the glow and the greeting
const GLOW = 'conic-gradient(from 0deg, #4285f4, #9b72cb, #d96570, #f9ab00, #4285f4)'
const GREETING = 'linear-gradient(74deg, #4285f4 0%, #9b72cb 30%, #d96570 60%, #9b72cb 100%)'

const spin = keyframes`
  from { transform: translate(-50%, -50%) rotate(0deg); }
  to { transform: translate(-50%, -50%) rotate(360deg); }
`

const pulse = keyframes`
  0%, 100% { opacity: 0.55; }
  50% { opacity: 0.9; }
`

// The moving glow: a large conic gradient spinning behind the window, clipped to a rounded frame a
// little bigger than the window. With `blur` it is the soft halo; without, the crisp edge.
function Glow({ inset, blur, radius }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        inset: -inset + 'px',
        borderRadius: radius + inset + 'px',
        overflow: 'hidden',
        pointerEvents: 'none',
        filter: blur ? 'blur(' + blur + 'px)' : 'none',
        animation: blur ? `${pulse} 4s ease-in-out infinite` : 'none',
        '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          width: '250vmax',
          height: '250vmax',
          background: GLOW,
          transform: 'translate(-50%, -50%)',
          animation: `${spin} 8s linear infinite`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />
    </Box>
  )
}

const EXAMPLES = [
  'Keitä on varattu huomenna klo 17–19?',
  'Mitkä ajat ovat vapaana ensi lauantaina?',
  'Paljonko myynti oli viime kuussa lajeittain?',
  'Mitä jäsenyyksiä meillä on myynnissä?',
]

const PROPOSAL_STATUS = {
  pending: { text: 'Odottaa hyväksyntää', color: 'warning.main' },
  executing: { text: 'Tehdään…', color: 'info.main' },
  executed: { text: 'Tehty', color: 'success.main' },
  rejected: { text: 'Hylätty', color: 'text.secondary' },
  expired: { text: 'Vanhentunut, ei tehty', color: 'text.secondary' },
  failed: { text: 'Epäonnistui', color: 'error.main' },
}

const errorText = (err) => {
  if (!err) return 'Tuntematon virhe.'
  if (err.details && err.details.reason === 'awaiting_approval') return 'Hyväksy tai hylkää ensin odottava muutosehdotus.'
  if (err.code === 'functions/deadline-exceeded') return 'Vastaus kesti liian kauan. Yritä uudelleen tai tarkenna kysymystä.'
  if (err.code === 'functions/permission-denied' || err.code === 'functions/unauthenticated') return 'Apuri ei ole käytössä tällä tunnuksella.'
  return err.message || String(err)
}

// consecutive tool lines and interim texts between the question and the answer form one "steps" group
const groupMessages = (messages) => {
  const out = []
  messages.forEach((m) => {
    if (m.role === 'proposal') {
      out.push({ proposal: m, key: 'proposal-' + m.seq })
      return
    }
    const isStep = m.role === 'tool' || (m.role === 'assistant' && m.interim)
    if (isStep) {
      const last = out[out.length - 1]
      if (last && last.steps) last.steps.push(m)
      else out.push({ steps: [m], key: 'steps-' + m.seq })
    } else {
      out.push({ message: m, key: String(m.seq) })
    }
  })
  return out
}

function Steps({ steps, live }) {
  const [open, setOpen] = useState(false)
  const tools = steps.filter((s) => s.role === 'tool')
  const last = steps[steps.length - 1]
  return (
    <Box sx={{ my: 0.5, color: 'text.secondary', fontSize: '0.85rem' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={() => setOpen(!open)}>
        <BuildIcon sx={{ fontSize: '0.95rem', mr: 0.5 }} />
        <span>
          {live && !open ? (last.summary || last.text) : tools.length + (tools.length === 1 ? ' haku' : ' hakua')}
        </span>
        {open ? <ExpandLessIcon fontSize='small' /> : <ExpandMoreIcon fontSize='small' />}
      </Box>
      {open ? (
        <Box sx={{ pl: 2.5, borderLeft: '2px solid #ddd', ml: 0.8 }}>
          {steps.map((s) => (
            <div key={s.seq} style={{ fontStyle: s.role === 'tool' ? 'normal' : 'italic' }}>
              {s.role === 'tool' ? s.summary || s.tool : s.text}
            </div>
          ))}
        </Box>
      ) : null}
    </Box>
  )
}

function Bubble({ message }) {
  if (message.role === 'user') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', my: 1 }}>
        <Box sx={{ bgcolor: '#e9eef6', borderRadius: '18px 4px 18px 18px', px: 2, py: 1.25, maxWidth: '80%', whiteSpace: 'pre-wrap' }}>{message.text}</Box>
      </Box>
    )
  }
  return (
    <Box
      sx={{
        my: 1,
        '& p': { my: 0.5 },
        '& table': { borderCollapse: 'collapse', my: 1, fontSize: '0.85rem', display: 'block', overflowX: 'auto' },
        '& th, & td': { border: '1px solid #ddd', px: 0.75, py: 0.25, textAlign: 'left', verticalAlign: 'top' },
        '& th': { bgcolor: '#f5f5f5' },
        '& ul, & ol': { pl: 2.5, my: 0.5 },
      }}
      dangerouslySetInnerHTML={{ __html: md.render(message.text || '') }}
    />
  )
}

// A write proposal as the server previewed it. Only these server facts are shown, never the model's text.
function ProposalCard({ proposal, busy, onDecide }) {
  if (!proposal) {
    return (
      <Box sx={{ my: 1 }}>
        <CircularProgress size={16} />
      </Box>
    )
  }
  const status = PROPOSAL_STATUS[proposal.status] || { text: proposal.status, color: 'text.secondary' }
  const pending = proposal.status === 'pending'
  return (
    <Box sx={{ my: 1.5, border: '1px solid', borderColor: pending ? 'warning.main' : '#ddd', borderRadius: 2, p: 1.5, bgcolor: pending ? '#fffaf0' : '#fafafa' }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5 }}>
        <Typography variant='subtitle2' sx={{ flexGrow: 1 }}>
          Muutosehdotus: {proposal.label}
        </Typography>
        <Typography variant='caption' sx={{ color: status.color, fontWeight: 'bold' }}>
          {status.text}
        </Typography>
      </Box>
      <Typography variant='body2' sx={{ mb: 1 }}>
        {proposal.summary}
      </Typography>
      <Box component='table' sx={{ fontSize: '0.85rem', borderCollapse: 'collapse', mb: 1, '& td': { py: 0.25, pr: 1.5, verticalAlign: 'top' } }}>
        <tbody>
          {(proposal.facts || []).map((f, i) => (
            <tr key={i}>
              <td style={{ color: '#666', whiteSpace: 'nowrap' }}>{f.label}</td>
              <td style={{ whiteSpace: 'pre-wrap' }}>{f.value}</td>
            </tr>
          ))}
        </tbody>
      </Box>
      {(proposal.warnings || []).map((w, i) => (
        <Alert key={i} severity='warning' sx={{ py: 0, mb: 0.5 }}>
          {w}
        </Alert>
      ))}
      {proposal.status === 'executed' && proposal.result ? <Alert severity='success' sx={{ py: 0 }}>{proposal.result}</Alert> : null}
      {proposal.status === 'failed' && proposal.error ? <Alert severity='error' sx={{ py: 0 }}>{proposal.error}</Alert> : null}
      {pending ? (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Button variant='contained' color='success' size='small' disabled={busy} onClick={() => onDecide('approve')}>
            {busy ? <CircularProgress size={16} color='inherit' /> : 'Hyväksy'}
          </Button>
          <Button variant='outlined' size='small' disabled={busy} onClick={() => onDecide('reject')}>
            Hylkää
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

function AssistantDrawer({ firebase, open, onClose }) {
  const [view, setView] = useState('chat') // 'chat' | 'list'
  const [conversationId, setConversationId] = useState(null)
  const [messages, setMessages] = useState([])
  const [input, setInput] = useState('')
  const [sending, setSending] = useState(false)
  const [error, setError] = useState(null)
  const [conversations, setConversations] = useState(null)
  const [usage, setUsage] = useState(null)
  const [confirmDelete, setConfirmDelete] = useState(null)
  const [proposals, setProposals] = useState({})
  const [resolving, setResolving] = useState(null)
  const [full, setFull] = useState(false)
  const endRef = useRef(null)

  // the open conversation's messages and its proposals
  useEffect(() => {
    setFull(false)
    if (!conversationId) {
      setMessages([])
      setProposals({})
      return undefined
    }
    const unsubscribe = firebase.listenAssistantMessages(conversationId).onSnapshot(
      (snap) => setMessages(snap.docs.map((d) => d.data())),
      (err) => setError(errorText(err))
    )
    const unsubscribeProposals = firebase.listenAssistantProposals(conversationId).onSnapshot(
      (snap) => {
        const all = {}
        snap.docs.forEach((d) => { all[d.id] = d.data() })
        setProposals(all)
      },
      (err) => setError(errorText(err))
    )
    return () => {
      unsubscribe()
      unsubscribeProposals()
    }
  }, [firebase, conversationId])

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' })
  }, [messages, sending, proposals])

  const waitingProposal = Object.keys(proposals).some((id) => proposals[id].status === 'pending')
  const busy = sending || Boolean(resolving)

  const decide = async (proposalId, decision) => {
    setError(null)
    setResolving(proposalId)
    try {
      await firebase.assistantResolve(conversationId, proposalId, decision)
    } catch (err) {
      setError(errorText(err))
    }
    setResolving(null)
    loadList()
  }

  const loadList = async () => {
    try {
      const res = await firebase.assistantList()
      setConversations(res.conversations || [])
      setUsage(res.usage || null)
    } catch (err) {
      setError(errorText(err))
    }
  }

  useEffect(() => {
    if (open) loadList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const send = async (textArg) => {
    const text = (textArg || input).trim()
    if (!text || busy || waitingProposal || full) return
    setError(null)
    setSending(true)
    setInput('')
    let id = conversationId
    const isNew = !id
    if (isNew) {
      id = firebase.newAssistantConversationId()
      setConversationId(id)
    }
    try {
      await firebase.assistantSend(text, isNew ? null : id, isNew ? id : null)
    } catch (err) {
      if (err.details && err.details.reason === 'conversation_full') setFull(true)
      else setError(errorText(err))
      if (isNew) setConversationId(null)
      setInput(text)
    }
    setSending(false)
    loadList()
  }

  const newConversation = () => {
    setConversationId(null)
    setError(null)
    setView('chat')
  }

  const openConversation = (id) => {
    setConversationId(id)
    setError(null)
    setView('chat')
  }

  const deleteConversation = async (id) => {
    setConfirmDelete(null)
    try {
      await firebase.assistantDelete(id)
      if (id === conversationId) setConversationId(null)
      loadList()
    } catch (err) {
      setError(errorText(err))
    }
  }

  const grouped = groupMessages(messages)
  const lastAnswered = messages.length > 0 && messages[messages.length - 1].role === 'assistant' && !messages[messages.length - 1].interim

  const fresh = !conversationId && !sending && view === 'chat'
  const user = firebase.auth && firebase.auth.currentUser
  const firstName = user && user.displayName ? user.displayName.split(' ')[0] : ''
  const radius = 24
  const canSend = Boolean(input.trim()) && !busy

  const inputBox = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 1,
        bgcolor: '#f0f4f9',
        borderRadius: '28px',
        pl: 2.5,
        pr: 1,
        py: 1,
        transition: 'background-color 0.2s, box-shadow 0.2s',
        '&:focus-within': { bgcolor: '#e9eef6', boxShadow: '0 0 0 2px rgba(66,133,244,0.25)' },
      }}
    >
      <TextField
        fullWidth
        multiline
        maxRows={8}
        variant='standard'
        placeholder={waitingProposal ? 'Hyväksy tai hylkää ensin muutosehdotus' : 'Kysy Apurilta'}
        value={input}
        disabled={busy || waitingProposal || full}
        autoFocus
        InputProps={{ disableUnderline: true, sx: { fontSize: '1rem', py: 0.75 } }}
        inputProps={{ maxLength: 4000 }}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            send()
          }
        }}
      />
      <IconButton
        onClick={() => send()}
        disabled={busy || waitingProposal || full || !input.trim()}
        sx={{
          bgcolor: canSend ? '#0b57d0' : 'transparent',
          color: canSend ? '#fff' : 'inherit',
          '&:hover': { bgcolor: canSend ? '#0842a0' : 'rgba(0,0,0,0.04)' },
        }}
      >
        {busy ? <CircularProgress size={22} /> : <SendIcon />}
      </IconButton>
    </Box>
  )

  return (
    <Modal
      open={open}
      onClose={onClose}
      closeAfterTransition
      slotProps={{ backdrop: { sx: { bgcolor: 'rgba(10, 14, 30, 0.6)', backdropFilter: 'blur(3px)' } } }}
    >
      <Fade in={open}>
        {/* Centred with a transform, so the window grows evenly up and down as its content does */}
        <Box
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: { xs: 'calc(100vw - 16px)', sm: 'min(760px, calc(100vw - 48px))' },
            maxHeight: { xs: 'calc(100vh - 16px)', sm: 'calc(100vh - ' + 2 * MARGIN + 'px)' },
            display: 'flex',
            outline: 'none',
          }}
        >
          <Glow inset={14} blur={28} radius={radius} />
          <Glow inset={2} blur={0} radius={radius} />
          <Box
            sx={{
              position: 'relative',
              flex: 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              bgcolor: '#fff',
              borderRadius: radius + 'px',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', px: 2, pt: 1, pb: fresh ? 0 : 1 }}>
              <AutoAwesomeIcon sx={{ fontSize: '1.1rem', mr: 0.75, color: '#9b72cb' }} />
              <Typography sx={{ flexGrow: 1, fontWeight: 500 }}>
                Apuri{' '}
                <Typography component='span' variant='caption' color='text.secondary'>
                  beta
                </Typography>
              </Typography>
              <Tooltip title='Uusi keskustelu'>
                <span>
                  <IconButton size='small' onClick={newConversation} disabled={busy}>
                    <AddIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Aiemmat keskustelut'>
                <span>
                  <IconButton size='small' onClick={() => setView(view === 'list' ? 'chat' : 'list')} disabled={busy}>
                    <HistoryIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title='Sulje'>
                <IconButton size='small' onClick={onClose}>
                  <CloseIcon />
                </IconButton>
              </Tooltip>
            </Box>
            {usage && usage.percent >= 80 ? (
              <Alert severity={usage.percent >= 100 ? 'error' : 'warning'} sx={{ mx: 2, mb: 1 }}>
                Kuukauden käyttökiintiöstä on käytetty {Math.min(usage.percent, 100)} %.
              </Alert>
            ) : null}

            {view === 'list' ? (
              <Box sx={{ flex: '1 1 auto', minHeight: 200, overflowY: 'auto', borderTop: '1px solid #eee' }}>
                {conversations === null ? (
                  <Box sx={{ p: 3, textAlign: 'center' }}>
                    <CircularProgress size={24} />
                  </Box>
                ) : conversations.length === 0 ? (
                  <Typography sx={{ p: 2 }} color='text.secondary'>
                    Ei aiempia keskusteluja.
                  </Typography>
                ) : (
                  <List dense>
                    {conversations.map((c) => (
                      <ListItem
                        key={c.id}
                        disablePadding
                        secondaryAction={
                          confirmDelete === c.id ? (
                            <Button size='small' color='error' onClick={() => deleteConversation(c.id)}>
                              Poista
                            </Button>
                          ) : (
                            <IconButton edge='end' onClick={() => setConfirmDelete(c.id)} title='Poista keskustelu'>
                              <DeleteIcon fontSize='small' />
                            </IconButton>
                          )
                        }
                      >
                        <ListItemButton selected={c.id === conversationId} onClick={() => openConversation(c.id)}>
                          <ListItemText primary={c.title} secondary={c.updated ? moment(c.updated).format('D.M.YYYY HH:mm') : null} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            ) : fresh ? (
              // A fresh chat, Gemini style: a greeting, the question box and a few starting points
              <Box sx={{ px: { xs: 2.5, sm: 5 }, pt: { xs: 3, sm: 5 }, pb: 3, overflowY: 'auto' }}>
                <Typography
                  component='h2'
                  sx={{
                    fontSize: { xs: '1.9rem', sm: '2.5rem' },
                    fontWeight: 500,
                    lineHeight: 1.2,
                    background: GREETING,
                    WebkitBackgroundClip: 'text',
                    backgroundClip: 'text',
                    color: 'transparent',
                    width: 'fit-content',
                  }}
                >
                  {firstName ? 'Hei, ' + firstName : 'Hei'}
                </Typography>
                <Typography sx={{ fontSize: { xs: '1.4rem', sm: '1.9rem' }, fontWeight: 500, color: '#c4c7c5', lineHeight: 1.25, mb: 3 }}>
                  Miten voin auttaa?
                </Typography>
                {inputBox}
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 1.25, mt: 2.5 }}>
                  {EXAMPLES.map((e) => (
                    <Box
                      key={e}
                      component='button'
                      type='button'
                      onClick={() => send(e)}
                      sx={{
                        textAlign: 'left',
                        border: 'none',
                        bgcolor: '#f0f4f9',
                        borderRadius: '16px',
                        px: 2,
                        py: 1.5,
                        fontSize: '0.9rem',
                        color: '#1f1f1f',
                        cursor: 'pointer',
                        transition: 'background-color 0.15s',
                        '&:hover': { bgcolor: '#dde3ea' },
                      }}
                    >
                      {e}
                    </Box>
                  ))}
                </Box>
                <Typography variant='body2' color='text.secondary' sx={{ mt: 2.5 }}>
                  Muutoksista (peruutus, maksu, saldo, jäsenyys, viesti) Apuri tekee ensin ehdotuksen, jonka hyväksyt tai hylkäät.
                </Typography>
                {error ? (
                  <Alert severity='error' sx={{ mt: 2 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                ) : null}
              </Box>
            ) : (
              <>
                <Box sx={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', px: { xs: 2, sm: 4 }, py: 1, borderTop: '1px solid #f0f0f0' }}>
                  {grouped.map((g, i) =>
                    g.steps ? (
                      <Steps key={g.key} steps={g.steps} live={busy && i === grouped.length - 1} />
                    ) : g.proposal ? (
                      <ProposalCard
                        key={g.key}
                        proposal={proposals[g.proposal.proposalId]}
                        busy={resolving === g.proposal.proposalId}
                        onDecide={(decision) => decide(g.proposal.proposalId, decision)}
                      />
                    ) : (
                      <Bubble key={g.key} message={g.message} />
                    )
                  )}
                  {busy && !lastAnswered ? <LinearProgress sx={{ my: 1, borderRadius: 1 }} /> : null}
                  {full ? (
                    <Alert
                      severity='info'
                      sx={{ my: 1 }}
                      action={
                        <Button color='inherit' size='small' onClick={newConversation}>
                          Uusi keskustelu
                        </Button>
                      }
                    >
                      Tämä keskustelu on täynnä. Aloita uusi keskustelu jatkaaksesi.
                    </Alert>
                  ) : null}
                  <div ref={endRef} />
                </Box>
                {error ? (
                  <Alert severity='error' sx={{ mx: 2, mb: 1 }} onClose={() => setError(null)}>
                    {error}
                  </Alert>
                ) : null}
                <Box sx={{ px: { xs: 1.5, sm: 3 }, pt: 1 }}>{inputBox}</Box>
                <Typography variant='caption' color='text.secondary' sx={{ textAlign: 'center', py: 1 }}>
                  Apuri voi erehtyä. Tarkista tärkeät tiedot.
                </Typography>
              </>
            )}
          </Box>
        </Box>
      </Fade>
    </Modal>
  )
}

export default AssistantDrawer
